import React, { useMemo, useCallback, useEffect } from 'react';
import { Accordion, AccordionItem, Chip } from '@heroui/react';
import { MdCategory } from 'react-icons/md';
import { RequiredInfo, RequiredInfoValue } from '@/util/types/serverTypes';
import RequiredInfoResolver from './RequiredInfoResolver';

interface RequiredInfoAccordionProps {
  // Raw data inputs
  allRequiredInfo: RequiredInfo[];
  requiredInfoIds: string[];
  values: RequiredInfoValue[];
  setValues: (values: RequiredInfoValue[]) => void;
  // Optional configuration
  excludeHidden?: boolean;
  colCount?: 2 | 3;
}

// Map colCount to Tailwind grid-cols classes (1 col default; md ramps up; 3xl adds a column on very wide viewports)
const gridColsMap: Record<2 | 3, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3 2xl:grid-cols-4',
};

export default function RequiredInfoAccordion({
  allRequiredInfo,
  requiredInfoIds,
  values,
  setValues,
  excludeHidden = true,
  colCount = 2,
}: RequiredInfoAccordionProps) {
  // Filter required info items by IDs
  const requiredInfoItems = useMemo(() => {
    if (!allRequiredInfo || !Array.isArray(allRequiredInfo)) return [];

    const itemsMap = new Map(allRequiredInfo.map((item) => [item._id, item]));

    return requiredInfoIds
      .map((id) => itemsMap.get(id))
      .filter(
        (item): item is RequiredInfo =>
          !!item && (!excludeHidden || !item.hidden)
      );
  }, [allRequiredInfo, requiredInfoIds, excludeHidden]);

  // Group required info items by category
  const groupedRequiredInfo = useMemo(() => {
    if (!requiredInfoItems) return {};

    const groups: Record<string, RequiredInfo[]> = {};

    requiredInfoItems.forEach((item) => {
      const category = item.category || 'No Category';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });

    // Sort each group by order
    Object.keys(groups).forEach((category) => {
      groups[category].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return groups;
  }, [requiredInfoItems]);

  // Seed defaults only for required info fields the user hasn't touched yet (entry missing
  // entirely). An existing entry with an empty value represents an explicit clear — respect
  // that and do NOT re-apply the default, otherwise required selects would appear "stuck"
  // on their default.
  //
  // Mandatory booleans are seeded to 'false' for the same reason with a different
  // twist: a Switch renders a definite off state and only emits a value when
  // toggled, so a required boolean whose honest answer is "No" would otherwise
  // have no answer to record — and once required info actually gates navigation
  // that would be a dead end. The rendered off state IS the answer. Booleans can
  // never be cleared back to empty, so this cannot fight the no-reseed rule above.
  const seedValueFor = useCallback((item: RequiredInfo): string | null => {
    if (item.default) return item.default;
    if (item.type === 'boolean' && item.required && !item.hidden) return 'false';
    return null;
  }, []);

  useEffect(() => {
    if (!requiredInfoItems || requiredInfoItems.length === 0) return;

    const existingIds = new Set(values.map((v) => v._requiredInfoId));
    const seeds = requiredInfoItems
      .filter((item) => !existingIds.has(item._id))
      .map((item) => ({ item, seed: seedValueFor(item) }))
      .filter((entry): entry is { item: RequiredInfo; seed: string } => !!entry.seed);

    if (seeds.length === 0) return;

    const newValues = [
      ...values,
      ...seeds.map<RequiredInfoValue>(({ item, seed }) => ({
        _requiredInfoId: item._id,
        name: item.name,
        value: seed,
      })),
    ];

    setValues(newValues);
  }, [requiredInfoItems, values, setValues, seedValueFor]);

  // Get the current value for a required info item
  const getValueForItem = useCallback(
    (itemId: string) => {
      const item = values.find((v) => v._requiredInfoId === itemId);
      return item?.value || null;
    },
    [values]
  );

  // Handle value changes from RequiredInfoResolver
  const handleValueChange = useCallback(
    (value: RequiredInfoValue) => {
      const newValues = [...values];
      const index = newValues.findIndex(
        (v) => v._requiredInfoId === value._requiredInfoId
      );

      if (index >= 0) {
        newValues[index] = value;
      } else {
        newValues.push(value);
      }

      setValues(newValues);
    },
    [values, setValues]
  );

  if (Object.keys(groupedRequiredInfo).length === 0) {
    return (
      <div className='flex h-24 items-center justify-center'>
        <p className='text-gray-500'>No required information found</p>
      </div>
    );
  }

  return (
    <Accordion
      variant='light'
      selectionMode='multiple'
      defaultExpandedKeys={Object.keys(groupedRequiredInfo)}
      className='rounded-xl! border bg-gray-100! shadow-none'
      itemClasses={{
        base: 'bg-gray-100',
        title: 'bg-gray-100',
        content: 'bg-gray-100 px-2',
        trigger: 'bg-gray-100',
      }}
    >
      {Object.entries(groupedRequiredInfo)
        .sort(([, itemsA], [, itemsB]) => {
          const minOrderA = Math.min(...itemsA.map((item) => item.order || 0));
          const minOrderB = Math.min(...itemsB.map((item) => item.order || 0));
          return minOrderA - minOrderB;
        })
        .map(([category, items]) => (
          <AccordionItem
            key={category}
            aria-label={category}
            title={
              <div className='flex items-center justify-between px-2'>
                <div className='flex items-center gap-2'>
                  <MdCategory className='text-default-500' size={18} />
                  <span className='text-sm font-medium'>{category}</span>
                </div>
                <Chip
                  size='sm'
                  variant='solid'
                  color='default'
                  className='text-xs'
                >
                  {items.length}
                </Chip>
              </div>
            }
          >
            <div
              className={`grid grid-cols-1 gap-x-2 gap-y-1 ${
                gridColsMap[colCount ?? 2]
              }`}
            >
              {items.map((item) => (
                <div key={item._id}>
                  <RequiredInfoResolver
                    requiredInfo={item}
                    onChange={handleValueChange}
                    value={getValueForItem(item._id)}
                  />
                </div>
              ))}
            </div>
          </AccordionItem>
        ))}
    </Accordion>
  );
}
